/**
 * Turning a chosen operation + the values a developer typed into the literal HTTP
 * request — the same request the console then sends, so what is shown as "the request"
 * and what actually goes over the wire are guaranteed to match (nothing is prettified
 * only for display).
 */
import { Operation, ParamDef, paramKey, trimSlash } from './contract';

export interface BuiltRequest {
	method: string;
	url: string;
	headers: Record<string, string>;
	bodyText?: string;
}

export interface BuildRequestArgs {
	baseUrl: string;
	op: Operation;
	paramValues: Record<string, any>;
	bodyValue: any;
	token?: string | null;
}

/** Path params left unfilled (still the literal `{name}` placeholder) — checked before
 *  sending so a missing id fails as "fill this in" rather than as a confusing 404. */
export function missingRequiredParams(op: Operation, paramValues: Record<string, any>): ParamDef[] {
	return op.parameters.filter(p => {
		if (!p.required) return false;
		const v = paramValues[paramKey(p)];
		return v === undefined || v === null || v === '';
	});
}

export function buildRequest({ baseUrl, op, paramValues, bodyValue, token }: BuildRequestArgs): BuiltRequest {
	let path = op.path;
	const query = new URLSearchParams();
	const headers: Record<string, string> = {};

	for (const p of op.parameters) {
		const v = paramValues[paramKey(p)];
		if (v === undefined || v === null || v === '') continue;
		if (p.in === 'path') {
			path = path.replace(`{${p.name}}`, encodeURIComponent(String(v)));
		} else if (p.in === 'query') {
			if (Array.isArray(v)) v.forEach(item => query.append(p.name, String(item)));
			else query.append(p.name, String(v));
		} else if (p.in === 'header') {
			headers[p.name] = String(v);
		}
	}

	let bodyText: string | undefined;
	if (op.requestBody) {
		headers['Content-Type'] = 'application/json';
		bodyText = JSON.stringify(bodyValue ?? {}, null, 2);
	}
	if (token && !op.anonymous) headers.Authorization = `Bearer ${token}`;

	const qs = query.toString();
	const url = `${trimSlash(baseUrl)}${path}${qs ? `?${qs}` : ''}`;
	return { method: op.method, url, headers, bodyText };
}

const shQuote = (s: string) => `'${s.replace(/'/g, `'\\''`)}'`;

export function toCurl(req: BuiltRequest): string {
	const lines = [`curl -X ${req.method} ${shQuote(req.url)} \\`];
	const headerEntries = Object.entries(req.headers);
	headerEntries.forEach(([k, v], i) => {
		const last = i === headerEntries.length - 1 && req.bodyText === undefined;
		lines.push(`  -H ${shQuote(`${k}: ${v}`)}${last ? '' : ' \\'}`);
	});
	if (req.bodyText !== undefined) {
		lines.push(`  -d ${shQuote(req.bodyText)}`);
	}
	return lines.join('\n');
}

export function toFetch(req: BuiltRequest): string {
	const headerLines = Object.entries(req.headers)
		.map(([k, v]) => `    ${JSON.stringify(k)}: ${JSON.stringify(v)},`)
		.join('\n');
	return `await fetch(${JSON.stringify(req.url)}, {
  method: ${JSON.stringify(req.method)},
  headers: {
${headerLines}
  },${
		req.bodyText !== undefined ? `\n  body: ${JSON.stringify(req.bodyText)},` : ''
	}
});`;
}

export function toNode(req: BuiltRequest): string {
	return `// Node 18+ — fetch is global, no import needed
const res = ${toFetch(req)}
const data = await res.json();
console.log(res.status, data);`;
}

/* ------------------------------------------------------------------ snippets
 *
 * One generator per language, all from the SAME BuiltRequest the console sends,
 * so a copied snippet reproduces the call exactly — headers, query string and
 * body included. Anything else is a snippet that works in the docs and not in
 * the client's editor.
 *
 * Each one is idiomatic for its language rather than a transliteration of curl:
 * a developer should be able to paste it into their project and recognise it as
 * something they would have written.
 */

/** A language's own string literal, escaped for that language. */
const dq = (s: string) => JSON.stringify(s);

export function toPython(req: BuiltRequest): string {
	const headers = Object.entries(req.headers)
		.map(([k, v]) => `    ${dq(k)}: ${dq(v)},`)
		.join('\n');
	const body =
		req.bodyText !== undefined ? `\n    data=${dq(req.bodyText)},` : '';
	return `import requests

response = requests.request(
    ${dq(req.method)},
    ${dq(req.url)},
    headers={
${headers}
    },${body}
)

print(response.status_code, response.json())`;
}

export function toRuby(req: BuiltRequest): string {
	const headers = Object.entries(req.headers)
		.map(([k, v]) => `request[${dq(k)}] = ${dq(v)}`)
		.join('\n');
	const body = req.bodyText !== undefined ? `\nrequest.body = ${dq(req.bodyText)}` : '';
	const klass = req.method.charAt(0) + req.method.slice(1).toLowerCase();
	return `require "net/http"
require "json"
require "uri"

uri = URI(${dq(req.url)})
request = Net::HTTP::${klass}.new(uri)
${headers}${body}

response = Net::HTTP.start(uri.hostname, uri.port, use_ssl: uri.scheme == "https") do |http|
  http.request(request)
end

puts response.code
puts JSON.parse(response.body)`;
}

export function toPhp(req: BuiltRequest): string {
	const headers = Object.entries(req.headers)
		.map(([k, v]) => `    ${dq(`${k}: ${v}`)},`)
		.join('\n');
	const body =
		req.bodyText !== undefined
			? `\n    CURLOPT_POSTFIELDS => ${dq(req.bodyText)},`
			: '';
	return `<?php
$ch = curl_init();
curl_setopt_array($ch, [
    CURLOPT_URL => ${dq(req.url)},
    CURLOPT_CUSTOMREQUEST => ${dq(req.method)},
    CURLOPT_RETURNTRANSFER => true,
    CURLOPT_HTTPHEADER => [
${headers}
    ],${body}
]);

$response = curl_exec($ch);
$status = curl_getinfo($ch, CURLINFO_HTTP_CODE);
curl_close($ch);

echo $status, PHP_EOL;
print_r(json_decode($response, true));`;
}

export function toJava(req: BuiltRequest): string {
	const headers = Object.entries(req.headers)
		.map(([k, v]) => `    .header(${dq(k)}, ${dq(v)})`)
		.join('\n');
	const body =
		req.bodyText !== undefined
			? `.method(${dq(req.method)}, HttpRequest.BodyPublishers.ofString(${dq(req.bodyText)}))`
			: `.method(${dq(req.method)}, HttpRequest.BodyPublishers.noBody())`;
	return `// Java 11+ — java.net.http, no dependencies
HttpRequest request = HttpRequest.newBuilder()
    .uri(URI.create(${dq(req.url)}))
${headers}
    ${body}
    .build();

HttpResponse<String> response = HttpClient.newHttpClient()
    .send(request, HttpResponse.BodyHandlers.ofString());

System.out.println(response.statusCode());
System.out.println(response.body());`;
}

export function toCsharp(req: BuiltRequest): string {
	const headers = Object.entries(req.headers)
		/* Content-Type belongs on the CONTENT in .NET, not on the request: adding
		   it here throws InvalidOperationException at runtime. */
		.filter(([k]) => k.toLowerCase() !== 'content-type')
		.map(([k, v]) => `request.Headers.Add(${dq(k)}, ${dq(v)});`)
		.join('\n');
	const body =
		req.bodyText !== undefined
			? `\nrequest.Content = new StringContent(${dq(req.bodyText)}, Encoding.UTF8, "application/json");`
			: '';
	return `using System.Net.Http;
using System.Text;

var client = new HttpClient();
var request = new HttpRequestMessage(new HttpMethod(${dq(req.method)}), ${dq(req.url)});
${headers}${body}

var response = await client.SendAsync(request);
Console.WriteLine((int)response.StatusCode);
Console.WriteLine(await response.Content.ReadAsStringAsync());`;
}

export function toGo(req: BuiltRequest): string {
	const headers = Object.entries(req.headers)
		.map(([k, v]) => `\treq.Header.Set(${dq(k)}, ${dq(v)})`)
		.join('\n');
	const body =
		req.bodyText !== undefined
			? `strings.NewReader(${dq(req.bodyText)})`
			: 'nil';
	const stringsImport = req.bodyText !== undefined ? '\n\t"strings"' : '';
	return `package main

import (
\t"fmt"
\t"io"
\t"net/http"${stringsImport}
)

func main() {
\treq, err := http.NewRequest(${dq(req.method)}, ${dq(req.url)}, ${body})
\tif err != nil {
\t\tpanic(err)
\t}
${headers}

\tres, err := http.DefaultClient.Do(req)
\tif err != nil {
\t\tpanic(err)
\t}
\tdefer res.Body.Close()

\tout, _ := io.ReadAll(res.Body)
\tfmt.Println(res.StatusCode, string(out))
}`;
}

export function toSwift(req: BuiltRequest): string {
	const headers = Object.entries(req.headers)
		.map(([k, v]) => `request.setValue(${dq(v)}, forHTTPHeaderField: ${dq(k)})`)
		.join('\n');
	const body =
		req.bodyText !== undefined
			? `\nrequest.httpBody = ${dq(req.bodyText)}.data(using: .utf8)`
			: '';
	return `import Foundation

var request = URLRequest(url: URL(string: ${dq(req.url)})!)
request.httpMethod = ${dq(req.method)}
${headers}${body}

let (data, response) = try await URLSession.shared.data(for: request)
print((response as? HTTPURLResponse)?.statusCode ?? 0)
print(String(data: data, encoding: .utf8) ?? "")`;
}

/** Every language the console offers, in the order it shows them. */
export const SNIPPET_LANGUAGES = [
	{ key: 'curl', label: 'cURL', render: toCurl },
	{ key: 'fetch', label: 'JavaScript', render: toFetch },
	{ key: 'node', label: 'Node', render: toNode },
	{ key: 'python', label: 'Python', render: toPython },
	{ key: 'ruby', label: 'Ruby', render: toRuby },
	{ key: 'php', label: 'PHP', render: toPhp },
	{ key: 'java', label: 'Java', render: toJava },
	{ key: 'csharp', label: 'C#', render: toCsharp },
	{ key: 'go', label: 'Go', render: toGo },
	{ key: 'swift', label: 'Swift', render: toSwift },
] as const;

export type SnippetLanguage = (typeof SNIPPET_LANGUAGES)[number]['key'];
